import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { Account, AccountReqDto, AccountResponse, AccountListResponse } from './account.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  private baseUrl = environment.contractApiURL + '/account';
  private accountsSubject = new BehaviorSubject<Account[]>([]);
  public accounts$ = this.accountsSubject.asObservable();

  constructor(private http: HttpClient) { }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json'
    });
  }

  private getAuthToken(): string {
    return localStorage.getItem('token') || '';
  }

  /**
   * Create new account using POST API
   */
  createAccount(accountDto: AccountReqDto): Observable<AccountResponse> {
    const token = this.getAuthToken();
    return this.http.post<AccountResponse>(`${this.baseUrl}/create?jwt=${token}`, accountDto, { 
      headers: this.getHeaders() 
    });
  }

  
  getAccounts(options?: {
    pageNo?: number;
    pageSize?: number;
    sortBy?: string;
    sortDir?: 'ASC' | 'DESC';
    status?: 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'BLACKLISTED';
    tenantId?: string;
  }): Observable<AccountListResponse> {
    // const token = this.getAuthToken();
    const params = new URLSearchParams();
    
    // params.append('jwt', token);
    
    params.append('pageNo', (options?.pageNo ?? 0).toString());
    params.append('pageSize', (options?.pageSize ?? 10).toString());
    
    params.append('sortBy', options?.sortBy ?? 'updatedAt');
    params.append('sortDir', options?.sortDir ?? 'DESC');
    
    if (options?.status) {
      params.append('status', options.status);
    }
    
    if (options?.tenantId) {
      params.append('tenantId', options.tenantId);
    }

    return this.http.get<AccountListResponse>(`${this.baseUrl}/read?${params.toString()}`, { 
      headers: this.getHeaders() 
    });
  }

  
  getAccountById(id: number): Observable<AccountResponse> {
    const token = this.getAuthToken();
    return this.http.get<AccountResponse>(`${this.baseUrl}/read?id=${id}&jwt=${token}`, { 
      headers: this.getHeaders() 
    });
  }

  /**
   * Update existing account
   */
  updateAccount(id: number, accountDto: AccountReqDto): Observable<AccountResponse> {
    const token = this.getAuthToken();
    return this.http.put<AccountResponse>(`${this.baseUrl}/update?id=${id}&jwt=${token}`, accountDto, { 
      headers: this.getHeaders() 
    });
  }

  /**
   * Delete account
   */
  deleteAccount(id: number): Observable<AccountResponse> {
    const token = this.getAuthToken();
    return this.http.delete<AccountResponse>(`${this.baseUrl}/${id}?jwt=${token}`, { 
      headers: this.getHeaders() 
    });
  }

  /**
   * Search accounts by term
   */
  searchAccounts(searchTerm: string): Observable<AccountListResponse> {
    const token = this.getAuthToken();
    return this.http.get<AccountListResponse>(`${this.baseUrl}/search?q=${encodeURIComponent(searchTerm)}&jwt=${token}`, { 
      headers: this.getHeaders() 
    });
  }

  /**
   * Filter accounts by status
   */
  filterAccountsByStatus(status: string): Observable<AccountListResponse> {
    const token = this.getAuthToken();
    return this.http.get<AccountListResponse>(`${this.baseUrl}/filter?status=${status}&jwt=${token}`, { 
      headers: this.getHeaders() 
    });
  }

  /**
   * Get accounts with pagination
   */
  getAccountsPaginated(page: number, pageSize: number, searchTerm?: string, status?: string): Observable<AccountListResponse> {
    const token = this.getAuthToken();
    let url = `${this.baseUrl}/paginated?page=${page}&pageSize=${pageSize}&jwt=${token}`;
    
    if (searchTerm) {
      url += `&search=${encodeURIComponent(searchTerm)}`;
    }
    
    if (status && status !== 'all') {
      url += `&status=${status}`;
    }

    return this.http.get<AccountListResponse>(url, { 
      headers: this.getHeaders() 
    });
  }

  /**
   * Update local accounts cache
   */
  updateAccountsCache(accounts: Account[]): void {
    this.accountsSubject.next(accounts);
  }

  /**
   * Add account to cache
   */
  addAccountToCache(account: Account): void {
    const currentAccounts = this.accountsSubject.value;
    this.accountsSubject.next([...currentAccounts, account]);
  }

  /**
   * Update account in cache
   */
  updateAccountInCache(updatedAccount: Account): void {
    const currentAccounts = this.accountsSubject.value;
    const index = currentAccounts.findIndex(acc => acc.id === updatedAccount.id);
    if (index !== -1) {
      currentAccounts[index] = updatedAccount;
      this.accountsSubject.next([...currentAccounts]);
    }
  }

  /**
   * Remove account from cache
   */
  removeAccountFromCache(accountId: number): void {
    const currentAccounts = this.accountsSubject.value;
    const filteredAccounts = currentAccounts.filter(acc => acc.id !== accountId);
    this.accountsSubject.next(filteredAccounts);
  }
}
